import { Overlay, OverlayConfig } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, Injectable, Injector, inject } from '@angular/core';
import { take, timer } from 'rxjs';
import { SnackBarComponent } from './snack-bar.component';
import { SNACK_BAR_CONFIG } from './snack-bar-config';

export interface SnackBarOptions {
  message: string;
  title?: string;
  type?: 'success' | 'error';
  verticalPosition?: 'top' | 'bottom';
  horizontalPosition?: 'left' | 'right';
}

@Injectable({
  providedIn: 'root',
})
export class SnackBarService {
  private overlay = inject(Overlay);
  private injector = inject(Injector);
  private readonly config = inject(SNACK_BAR_CONFIG, { optional: true });
  private _openSnackBar?: ComponentRef<SnackBarComponent>;

  show(options: SnackBarOptions): void {
    if (this._openSnackBar) {
      this._openSnackBar.instance.destroy$.next(true);
    }
    this.attachSnackBar(options);
  }

  private getPositionStrategy({
    verticalPositionParam,
    horizontalPositionParam,
  }: {
    verticalPositionParam?: 'top' | 'bottom';
    horizontalPositionParam?: 'left' | 'right';
  }) {
    const verticalPosition =
      verticalPositionParam ?? this.config?.defaultVerticalPosition ?? 'bottom';
    const horizontalPosition =
      horizontalPositionParam ??
      this.config?.defaultHorizontalPosition ??
      'left';
    return this.overlay
      .position()
      .global()
      [verticalPosition]('1rem')
      [horizontalPosition]('1rem');
  }

  // Utilizes Material CDK Overlay to attach the SnackBarComponent to the DOM
  private attachSnackBar({
    message,
    title,
    type,
    verticalPosition,
    horizontalPosition,
  }: SnackBarOptions): void {
    const overlayConfig = new OverlayConfig({
      disposeOnNavigation: false,
      positionStrategy: this.getPositionStrategy({
        verticalPositionParam: verticalPosition,
        horizontalPositionParam: horizontalPosition,
      }),
    });
    const overlayRef = this.overlay.create(overlayConfig);
    const containerPortal = new ComponentPortal(
      SnackBarComponent,
      undefined,
      this.injector
    );
    const containerRef = overlayRef.attach(containerPortal);
    containerRef.setInput('message', message);
    containerRef.setInput('title', title ?? '');
    containerRef.setInput('type', type ?? 'success');
    this._openSnackBar = containerRef;
    this.listenForSnackBarCloseOrTimeout();
  }

  private listenForSnackBarCloseOrTimeout(): void {
    const instance = this._openSnackBar!.instance;
    const timeoutSub = timer(3000).subscribe(() => instance.close());
    instance.destroy$.pipe(take(1)).subscribe(() => {
      timeoutSub.unsubscribe();
      this._openSnackBar?.destroy();
      this._openSnackBar = undefined;
    });
  }
}
